/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useMemo } from 'react';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { ABodySize, MButton, MButtonType, MButtonSize } from '@marriott/mi-web-ui-kit';
import { buildChatAvailabilityUrl } from '@marriott/mi-shop-components';
import { usePageContext } from '@marriott/mi-shop-components';
import { MChipGroup } from '../MChipGroup';
import { MResponseActions } from '../MResponseActions';
import * as Styled from './MDefaultTemplate.styles';
import { MDefaultTemplateProps } from './MDefaultTemplate.types';
import { useHqvMarkdownComponents } from '../../hooks/useHqvMarkdownComponents';
import { getRawSearchEdgesTotal, getRawSearchTotal } from '../../utils';

export const MDefaultTemplate: React.FC<MDefaultTemplateProps> = ({
  uiData,
  viewData,
  onChipClick,
  onOpenHotelPanel,
  onViewAllProperties,
  messageId,
  thumbFeedback,
  onThumbFeedback,
}) => {
  const content = typeof uiData?.content === 'string' ? uiData.content : '';
  const followUp = typeof uiData?.followUp === 'string' ? uiData.followUp : '';
  const chips = Array.isArray(uiData?.mChipGroup?.mQuickSelectChip) ? uiData.mChipGroup!.mQuickSelectChip : [];
  const cta = uiData?.cta;

  // Pass message-specific viewData to ensure HQV links use correct prices
  const hqvComponents = useHqvMarkdownComponents(onOpenHotelPanel, viewData);

  // Page-level config for URL base
  const { NEXT_PUBLIC_AVAILABILITY_SEARCH_URL } = usePageContext();

  // Build the availability URL from the CTA payload and its captured search criteria snapshot.
  // The snapshot is captured when the CTA is created (in SSEEventManager) to ensure
  // stable URL construction regardless of current search form state or conversation switches.
  // Priority: CTA fields > snapshot fields (CTA has the most accurate data from TIP)
  const ctaUrl = useMemo(() => {
    if (!cta?.id || !NEXT_PUBLIC_AVAILABILITY_SEARCH_URL) return null;

    const snapshot = cta.searchCriteriaSnapshot;

    // Use CTA dates if available, otherwise fall back to snapshot dates
    const checkInDate = cta.fromDate ?? snapshot?.checkInDate;
    const checkOutDate = cta.toDate ?? snapshot?.checkOutDate;

    // Determine if this is a flexible date search
    // Priority: userSpecifiedDate from TIP response > snapshot.flexibleDate from FE search criteria
    const isFlexDate =
      cta.userSpecifiedDate !== undefined
        ? !cta.userSpecifiedDate // userSpecifiedDate true → isFlexDate false, and vice versa
        : (snapshot?.flexibleDate ?? (!checkInDate && !checkOutDate));

    // Extract rate codes from rateRequestTypes array
    // Maps TIP rate types to URL clusterCode/corporateCode
    let clusterCode: string | undefined;
    let corporateCode: string | undefined;
    let groupCode: string | undefined;
    let usePointsFromRateType = false;

    const rateRequest = cta.rateRequestTypes?.[0];
    if (rateRequest) {
      const rateType = rateRequest.type?.toUpperCase();
      const rateValue = rateRequest.value;

      switch (rateType) {
        case 'AAA':
          clusterCode = 'aaa';
          break;
        case 'GOV':
          clusterCode = 'gov';
          break;
        case 'CLUSTER':
          // CLUSTER type uses the value directly
          if (rateValue) {
            // Points-related cluster codes
            const pointsClusterCodes = ['MRW', 'P17'];
            // Known non-corporate cluster codes
            const knownClusterCodes = ['S9R', 's9r', 'aaa', 'AAA', 'gov', 'GOV'];

            if (pointsClusterCodes.includes(rateValue.toUpperCase())) {
              // MRW, P17 are points rates
              usePointsFromRateType = true;
            } else if (knownClusterCodes.includes(rateValue)) {
              clusterCode = rateValue;
            } else {
              // Treat as corporate code
              clusterCode = 'corp';
              corporateCode = rateValue;
            }
          }
          break;
        case 'GROUP':
          clusterCode = 'grp';
          groupCode = rateValue;
          break;
        case 'POINTS':
        case 'REWARDS':
          // Points/Rewards rate - set flag to ensure useRewardsPoints is true
          usePointsFromRateType = true;
          break;
        // STANDARD and other types don't need clusterCode - they use standard rates
      }
    }

    // Fall back to CTA's direct clusterCode only (no snapshot fallback for rate types)
    if (!clusterCode && !corporateCode && cta.clusterCode) {
      // CTA has direct clusterCode (e.g., for CLUSTER type rates)
      const pointsClusterCodes = ['MRW', 'P17'];
      const knownClusterCodes = ['S9R', 's9r', 'aaa', 'AAA', 'gov', 'GOV'];

      if (pointsClusterCodes.includes(cta.clusterCode.toUpperCase())) {
        usePointsFromRateType = true;
      } else if (knownClusterCodes.includes(cta.clusterCode)) {
        clusterCode = cta.clusterCode;
      } else {
        clusterCode = 'corp';
        corporateCode = cta.clusterCode;
      }
    }

    // Use childAges from CTA only (default to empty array)
    const childrenAges = cta.childAges ?? [];
    const numChildrenPerRoom = childrenAges.length;
    const adultsPerRoom = (cta.guestCount ?? 1) - childrenAges.length;

    // Determine useRewardsPoints from CTA only (no snapshot fallback)
    const useRewardsPoints = usePointsFromRateType || cta.useRewardPoints || false;

    return buildChatAvailabilityUrl({
      baseUrl: NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      propertyCode: cta.id,
      chatSearchCriteria: {
        checkInDate,
        checkOutDate,
        flexibleDate: isFlexDate,
        numRooms: cta.roomCount ?? 1,
        numAdultsPerRoom: adultsPerRoom,
        numChildrenPerRoom,
        childrenAges,
        useRewardsPoints,
        clusterCode,
        corporateCode,
        groupCode,
      },
      forceRateCalendar: isFlexDate,
    });
  }, [cta, NEXT_PUBLIC_AVAILABILITY_SEARCH_URL]);

  const chipItems = useMemo(
    () =>
      chips.map((chip: { text?: string }) => ({
        aBody: {
          bodyText: chip?.text || '',
          size: ABodySize.S,
        },
        onClick: () => {
          if (chip?.text) {
            onChipClick?.(chip.text);
          }
        },
      })),
    [chips, onChipClick]
  );

  // Calculate total results from NLS search data for "View All Properties" button.
  // Only show the button when there are actual hotel results in this message's response.
  // Hide for single property response (availability_calendar_search).
  const totalResults = useMemo(() => {
    if (!viewData) return 0;
    return (
      getRawSearchEdgesTotal(viewData as any) ?? getRawSearchTotal(viewData as any) ?? 0
    );
  }, [viewData]);

  // Hide View All Properties for single property response
  const isSinglePropertyResponse = (viewData as any)?.isSinglePropertyResponse === true;
  const showViewAllProperties = Boolean(totalResults > 0 && onViewAllProperties && !isSinglePropertyResponse);

  return (
    <Styled.Container>
      {content && (
        <div className="t-font-s">
          <MarkdownPreview source={content} components={hqvComponents} />
        </div>
      )}

      <MResponseActions
        messageId={messageId}
        allowViewAllProperties={showViewAllProperties}
        viewAllCopy="categoryProperties"
        totalResults={totalResults}
        thumbFeedback={thumbFeedback}
        onThumbsUp={
          onThumbFeedback
            ? () => {
                if (thumbFeedback) return;
                onThumbFeedback('up');
              }
            : undefined
        }
        onThumbsDown={
          onThumbFeedback
            ? () => {
                if (thumbFeedback) return;
                onThumbFeedback('down');
              }
            : undefined
        }
        onViewAll={showViewAllProperties ? () => onViewAllProperties?.(viewData!) : undefined}
      />

      {cta && ctaUrl && (
        <div style={{ width: 'fit-content' }}>
          <MButton buttonType={MButtonType.Primary} size={MButtonSize.S} ctaLink={ctaUrl} target="_blank">
            {cta.text}
          </MButton>
        </div>
      )}

      {followUp && (
        <div className="t-font-s">
          <MarkdownPreview source={followUp} components={hqvComponents} />
        </div>
      )}

      {chipItems.length > 0 && (
        <Styled.ChipsWrapper>
          <MChipGroup chips={chipItems} variant="chat" />
        </Styled.ChipsWrapper>
      )}
    </Styled.Container>
  );
};

export type { MDefaultTemplateProps } from './MDefaultTemplate.types';
